import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CreditNoteQueryMetadata } from '~/api/creditNotes';
import type { CreditNote, CreditNoteStatus } from '~/api/creditNotes/types';
import type { Brand } from '~/types/user';

import { useClientsWithTrashed } from '~/api/clients';
import { creditNotesQueryOptions, useCreditNoteFilters } from '~/api/creditNotes';
import { useUser } from '~/api/user';
import { createAppColumnHelper, Table } from '~/components';
import { ColumnIds } from '~/constants/table';
import { useIntl } from '~/hooks/useIntl';
import { noop } from '~/utils/functions';

import { CreditNoteClientCell } from './CreditNotesTableCells/CreditNoteClientCell';
import { CreditNoteDocumentDateCell } from './CreditNotesTableCells/CreditNoteDocumentDateCell';
import { CreditNoteMobileCell } from './CreditNotesTableCells/CreditNoteMobileCell';
import { CreditNoteOptionsCell } from './CreditNotesTableCells/CreditNoteOptionsCell';
import { CreditNoteReasonCell } from './CreditNotesTableCells/CreditNoteReasonCell';
import { CreditNoteStatusCell } from './CreditNotesTableCells/CreditNoteStatusCell';
import { CreditNoteTitleCell } from './CreditNotesTableCells/CreditNoteTitleCell';
import { CreditNoteTotalCell } from './CreditNotesTableCells/CreditNoteTotalCell';

const columnHelper = createAppColumnHelper<CreditNote>();

export const useCreditNotesTableColumns = () => {
  const { data: clients } = useClientsWithTrashed();
  const filters = useCreditNotesTableFilters();

  const { t } = useTranslation(['creditnotes', 'filters']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Selector
        columnHelper.display({
          id: ColumnIds.selector,
          header: () => <Table.SelectAll />,
          cell: ({ row }) => <Table.SelectRow row={row} />,
          meta: {
            styles: { size: 'max-content' },
          },
        }),

        // Description & entry number
        columnHelper.accessor(({ description, fcEntryNumber }) => `${description} ${fcEntryNumber}`, {
          id: ColumnIds.creditNotesDescription,
          header: t('creditnotes:overview.columns.entryNumber'),
          cell: ({ row: { original: creditNote } }) => <CreditNoteTitleCell creditNote={creditNote} />,
          meta: {
            styles: { size: '3fr', minSize: 150 },
            isResizable: true,
          },
        }),

        // Brand (hidden, column filter only)
        columnHelper.accessor(({ brandId }) => brandId, {
          id: ColumnIds.creditNotesBrand,
          meta: {
            filterOptions: filters.brands,
          },
        }),

        // Client
        columnHelper.accessor(({ clientId }) => clients.find((client) => client.id === clientId)?.name ?? '', {
          id: ColumnIds.creditNotesClient,
          header: t('creditnotes:overview.columns.client'),
          cell: ({ row: { original: creditNote } }) => <CreditNoteClientCell creditNote={creditNote} />,
          meta: {
            styles: { size: '2fr', minSize: 125 },
            isResizable: true,
            filterOptions: filters.clients,
          },
        }),

        // Doc date
        columnHelper.accessor(({ documentDate }) => documentDate, {
          id: ColumnIds.creditNotesDocDate,
          header: t('creditnotes:overview.columns.date'),
          cell: ({ row: { original: creditNote } }) => <CreditNoteDocumentDateCell creditNote={creditNote} />,
          meta: {
            styles: { size: 'max-content', minSize: 100 },
            isResizable: true,
          },
        }),

        // Doc year (hidden, column filter only)
        columnHelper.accessor(({ documentDate }) => dayjs(documentDate).year(), {
          id: ColumnIds.creditNotesDocYear,
          meta: {
            filterOptions: filters.years,
          },
        }),

        // Doc quarter (hidden, column filter only)
        columnHelper.accessor(({ documentDate }) => `${dayjs(documentDate).year()} ${dayjs(documentDate).quarter()}`, {
          id: ColumnIds.creditNotesDocQuarter,
          meta: {
            filterOptions: filters.quarters,
          },
        }),

        // Reason
        columnHelper.accessor('reason', {
          id: ColumnIds.creditNotesReason,
          header: t('creditnotes:overview.columns.reason'),
          cell: ({ getValue }) => <CreditNoteReasonCell reason={getValue()} />,
          meta: {
            styles: { size: '3fr', minSize: 100 },
            isResizable: true,
          },
        }),

        // Status
        columnHelper.accessor((creditNote) => creditNote.status, {
          id: ColumnIds.creditNotesStatus,
          header: t('creditnotes:overview.columns.status.title'),
          cell: ({ row: { original: creditNote } }) => <CreditNoteStatusCell creditNote={creditNote} />,
          meta: {
            styles: { size: 'max-content', minSize: 120 },
            filterOptions: filters.statuses,
          },
        }),

        // Total
        columnHelper.accessor(({ calculationData }) => -1 * calculationData.fcExclVat, {
          id: ColumnIds.creditNotesTotal,
          header: t('creditnotes:overview.columns.total'),
          cell: ({ getValue }) => <CreditNoteTotalCell value={getValue()} />,
          meta: {
            styles: { size: 'max-content', minSize: 110, justify: 'end' },
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: creditNote } }) => <CreditNoteOptionsCell creditNote={creditNote} />,
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.creditNotesMobile,
          cell: ({ row: { original: creditNote } }) => <CreditNoteMobileCell creditNote={creditNote} />,
          meta: { mobile: true },
        }),
      ]),
    [clients, filters, t],
  );
};

export const usePrefetchNextPage = (nextPage: number | null, queryMetadata: CreditNoteQueryMetadata) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (nextPage === null) return;

    queryClient
      .query(
        creditNotesQueryOptions({
          ...queryMetadata,
          pagination: { ...queryMetadata.pagination, pageIndex: queryMetadata.pagination.pageIndex + 1 },
        }),
      )
      .catch(noop);
  }, [nextPage, queryClient, queryMetadata]);
};

const useCreditNotesTableFilters = () => {
  const { data: user } = useUser();
  const { data: clients } = useClientsWithTrashed();
  const { data: creditNoteFilters } = useCreditNoteFilters();

  const { compareForSort } = useIntl();
  const { t } = useTranslation(['filters', 'creditnotes']);

  const clientFilterOptions = useMemo(
    () =>
      creditNoteFilters.clientIds
        .map((clientId) => ({
          value: clientId,
          label: clients.find((client) => client.id === clientId)?.name ?? '',
        }))
        .sort((a, b) => compareForSort(a.label, b.label)),
    [clients, compareForSort, creditNoteFilters.clientIds],
  );

  const brandFilterOptions = useMemo(
    () =>
      creditNoteFilters.brandIds
        .map((brandId) => user.brands.find((brand) => brand.id === brandId) ?? null)
        .filter((brand): brand is Brand => brand !== null)
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || compareForSort(a.companyName, b.companyName))
        .map((brand) => ({ value: brand.id, label: brand.companyName })),
    [compareForSort, creditNoteFilters.brandIds, user.brands],
  );

  const yearFilterOptions = useMemo(
    () =>
      creditNoteFilters.years
        .map((year) => ({
          value: year,
          label: `${year}`,
        }))
        .sort((a, b) => -1 * compareForSort(a.label, b.label)),
    [creditNoteFilters.years, compareForSort],
  );

  const quarterFilterOptions = useMemo(
    () =>
      creditNoteFilters.quarters
        .map((yearQuarter) => ({
          value: yearQuarter,
          label: t('filters:quarter.option', { year: yearQuarter.split('-')[0], quarter: yearQuarter.split('-')[1] }),
        }))
        .sort((a, b) => {
          const [yearA, quarterA] = a.value.split('-').map((value) => +value);
          const [yearB, quarterB] = b.value.split('-').map((value) => +value);

          return yearB === yearA ? quarterB - quarterA : yearB - yearA;
        }),
    [creditNoteFilters.quarters, t],
  );

  const statusFilterOptions = useMemo(
    () =>
      creditNoteFilters.statuses
        .map((status) => ({
          value: status,
          label: t(`creditnotes:overview.columns.status.options.${status}`),
        }))
        .sort((a, b) => {
          const order: CreditNoteStatus[] = ['InReview', 'Approved'];
          return order.indexOf(a.value) - order.indexOf(b.value);
        }),
    [creditNoteFilters.statuses, t],
  );

  return useMemo(
    () => ({
      clients: clientFilterOptions,
      brands: brandFilterOptions,
      years: yearFilterOptions,
      quarters: quarterFilterOptions,
      statuses: statusFilterOptions,
    }),
    [brandFilterOptions, clientFilterOptions, quarterFilterOptions, statusFilterOptions, yearFilterOptions],
  );
};
